import * as React from "react";
import { useQuery, useMutation } from "convex/react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Boxes,
  Check,
  DollarSign,
  Download,
  Percent,
  Scale,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { InternalMetricCard } from "@/components/internal/shared/InternalMetricCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ENV } from "@/env";
import { api } from "@/lib/convexApi";
import {
  exportToCSV,
  formatCurrencyForExport,
  type ExportColumn,
} from "@/lib/export";
import { formatCurrency } from "@/lib/format";
import { getSalesRepBadgeClass } from "@/lib/sales-tag-colors";
import { cn } from "@/lib/utils";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";

interface CommissionsTabProps {
  authUserId?: string;
  initialMonth?: string;
}

interface CommissionRow {
  id: string;
  userId: string;
  origin: string;
  poNumber: string;
  bags: number;
  dateOfSale: number;
  clientName: string;
  company: string;
  isEnterprise: boolean;
  saleNumber: string;
  rep: string;
  bagWeight: number;
  totalWeight: number;
  salePrice: number;
  totalDollars: number;
  purchasePrice: number;
  difference: number;
  totalPurchase: number;
  profit: number;
}

function buildMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en-US", { month: "long", year: "numeric" });
    options.push({ value, label });
  }
  return options;
}

function getMonthRange(monthValue: string): { start: number; end: number } {
  const parts = monthValue.split("-").map(Number);
  const year = parts[0]!;
  const month = parts[1]!;
  const start = new Date(year, month - 1, 1).getTime();
  const end = new Date(year, month, 0, 23, 59, 59, 999).getTime();
  return { start, end };
}

const TAG_TONES = [
  "border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-200",
  "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-200",
  "border-orange-300 bg-orange-100 text-orange-900 dark:border-orange-800/60 dark:bg-orange-950/40 dark:text-orange-200",
  "border-cyan-300 bg-cyan-100 text-cyan-900 dark:border-cyan-800/60 dark:bg-cyan-950/40 dark:text-cyan-200",
] as const;

function pickTagTone(value: unknown): string {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (!normalized) return TAG_TONES[0];
  let hash = 0;
  for (const char of normalized) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return TAG_TONES[hash % TAG_TONES.length]!;
}

function profitBadgeClass(value: number): string {
  if (value > 0) {
    return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
  }
  if (value < 0) {
    return "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200";
  }
  return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200";
}

function marginBadgeClass(value: number): string {
  if (value >= 25) {
    return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
  }
  if (value >= 10) {
    return "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200";
  }
  if (value < 0) {
    return "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200";
  }
  return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200";
}

const ASSIGNABLE_REPS = [
  { code: "DS", label: "Daniel" },
  { code: "BLP", label: "Brian" },
  { code: "JEH", label: "Jon" },
  { code: "Sr", label: "Jeff" },
  { code: "KC", label: "Kris" },
  { code: "yes", label: "Needs Someone" },
  { code: "no", label: "Don't Need" },
] as const;

function RepAssignmentCell({
  row,
  authUserId,
}: {
  row: CommissionRow;
  authUserId: string | undefined;
}) {
  const [open, setOpen] = React.useState(false);
  const assignRep = useMutation(api.admin.assignSalesRep);

  const handleAssign = React.useCallback(
    async (code: string) => {
      if (!authUserId) return;
      setOpen(false);
      try {
        await assignRep({
          authUserId,
          userId: row.userId as any,
          salesRepId: code,
        });
        toast.success(`Rep assigned to ${ASSIGNABLE_REPS.find((r) => r.code === code)?.label ?? code}`);
      } catch (err: any) {
        toast.error(err.message ?? "Failed to assign rep");
      }
    },
    [assignRep, authUserId, row.userId],
  );

  if (!authUserId) {
    return (
      <Badge variant="outline" className={getSalesRepBadgeClass(row.rep)}>
        {row.rep || "Unassigned"}
      </Badge>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Badge variant="outline" className={cn(getSalesRepBadgeClass(row.rep), "cursor-pointer")}>
            {row.rep || "Unassigned"}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search reps..." />
          <CommandList className="max-h-full">
            <CommandEmpty>No reps found.</CommandEmpty>
            <CommandGroup className="max-h-[300px] scroll-py-1 overflow-y-auto overflow-x-hidden">
              {ASSIGNABLE_REPS.map((rep) => {
                const isCurrentRep = row.rep === rep.label;
                return (
                  <CommandItem
                    key={rep.code}
                    onSelect={() => handleAssign(rep.code)}
                  >
                    <div
                      className={cn(
                        "flex size-4 items-center justify-center rounded-sm border border-primary",
                        isCurrentRep
                          ? "bg-primary"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check />
                    </div>
                    <Badge variant="outline" className={cn(getSalesRepBadgeClass(rep.label), "text-xs")}>
                      {rep.label}
                    </Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function useColumns(data: CommissionRow[], authUserId?: string) {
  const originOptions = React.useMemo(() => {
    const origins = [...new Set(data.map((r) => r.origin).filter(Boolean))].sort();
    return origins.map((o) => ({ label: o, value: o }));
  }, [data]);

  const repOptions = React.useMemo(() => {
    const reps = [...new Set(data.map((r) => r.rep).filter(Boolean))].sort();
    return reps.map((r) => ({ label: r, value: r }));
  }, [data]);

  return React.useMemo<ColumnDef<CommissionRow>[]>(() => [
    {
      accessorKey: "origin",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Origin" />
      ),
      cell: ({ row }) => (
        <Badge variant="outline" className={pickTagTone(row.original.origin)}>
          {row.original.origin || "Unknown"}
        </Badge>
      ),
      meta: { label: "Origin", variant: "select" as const, options: originOptions },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) =>
        filterValues.some((v) => v === row.original.origin),
    },
    {
      accessorKey: "poNumber",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="PO#" />
      ),
      meta: { label: "PO#", variant: "text" as const },
      enableColumnFilter: true,
    },
    {
      id: "spacer",
      header: () => null,
      cell: () => null,
      size: 16,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "bags",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Bags" />
      ),
      cell: ({ row }) => row.original.bags.toLocaleString(),
      meta: { label: "Bags", variant: "number" as const },
      enableColumnFilter: true,
    },
    {
      accessorKey: "dateOfSale",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Date of Sale" />
      ),
      cell: ({ row }) =>
        new Date(row.original.dateOfSale).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
      meta: { label: "Date of Sale", variant: "dateRange" as const },
      enableColumnFilter: true,
    },
    {
      accessorKey: "clientName",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Client" />
      ),
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="text-xs font-medium">
            {row.original.company || row.original.clientName}
          </span>
          {row.original.company && row.original.company !== row.original.clientName && (
            <span className="text-xs text-muted-foreground">
              {row.original.clientName}
            </span>
          )}
        </div>
      ),
      filterFn: (row, _columnId, filterValue) => {
        if (!filterValue) return true;
        const search = String(filterValue).toLowerCase();
        const clientName = (row.original.clientName ?? "").toLowerCase();
        const company = (row.original.company ?? "").toLowerCase();
        return clientName.includes(search) || company.includes(search);
      },
      meta: { label: "Client", variant: "text" as const },
      enableColumnFilter: true,
    },
    {
      id: "isEnterprise",
      accessorKey: "isEnterprise",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Enterprise" />
      ),
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className={getEnterpriseBadgeClass(row.original.isEnterprise)}
        >
          {getEnterpriseBadgeLabel(row.original.isEnterprise)}
        </Badge>
      ),
      meta: {
        label: "Enterprise",
        variant: "select" as const,
        options: [
          { label: "Enterprise", value: "true" },
          { label: "Standard", value: "false" },
        ],
      },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) =>
        filterValues.some(
          (value: string) =>
            value === String(Boolean(row.original.isEnterprise)),
        ),
    },
    {
      accessorKey: "saleNumber",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Sale #" />
      ),
      meta: { label: "Sale #", variant: "text" as const },
      enableColumnFilter: true,
    },
    {
      accessorKey: "rep",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Rep" />
      ),
      cell: ({ row }) => (
        <RepAssignmentCell row={row.original} authUserId={authUserId} />
      ),
      meta: { label: "Rep", variant: "select" as const, options: repOptions },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) =>
        filterValues.some((v) => v === row.original.rep),
    },
    {
      accessorKey: "bagWeight",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Bag Wt" />
      ),
      cell: ({ row }) => `${row.original.bagWeight.toLocaleString()} lb`,
      meta: { label: "Bag Wt", variant: "number" as const },
    },
    {
      accessorKey: "totalWeight",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Total Wt" />
      ),
      cell: ({ row }) => `${row.original.totalWeight.toLocaleString()} lb`,
      meta: { label: "Total Wt", variant: "number" as const },
    },
    {
      accessorKey: "salePrice",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Sale Price" />
      ),
      cell: ({ row }) => formatCurrency(row.original.salePrice),
      meta: { label: "Sale Price", variant: "number" as const },
    },
    {
      accessorKey: "totalDollars",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Total Dollars" />
      ),
      cell: ({ row }) => formatCurrency(row.original.totalDollars),
      meta: { label: "Total Dollars", variant: "number" as const },
    },
    {
      accessorKey: "purchasePrice",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Purchase Price" />
      ),
      cell: ({ row }) => {
        const pp = row.original.purchasePrice;
        if (pp === 0) {
          return (
            <Badge
              variant="outline"
              className="border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200"
            >
              {formatCurrency(pp)}
            </Badge>
          );
        }
        return formatCurrency(pp);
      },
      meta: { label: "Purchase Price", variant: "number" as const },
    },
    {
      accessorKey: "difference",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Difference" />
      ),
      cell: ({ row }) => {
        const diff = row.original.difference;
        return (
          <Badge variant="outline" className={profitBadgeClass(diff)}>
            {formatCurrency(diff)}
          </Badge>
        );
      },
      meta: { label: "Difference", variant: "number" as const },
    },
    {
      accessorKey: "totalPurchase",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Total Purchase" />
      ),
      cell: ({ row }) => formatCurrency(row.original.totalPurchase),
      meta: { label: "Total Purchase", variant: "number" as const },
    },
    {
      accessorKey: "profit",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Profit" />
      ),
      cell: ({ row }) => {
        const p = row.original.profit;
        return (
          <Badge variant="outline" className={profitBadgeClass(p)}>
            {formatCurrency(p)}
          </Badge>
        );
      },
      meta: { label: "Profit", variant: "number" as const },
    },
  ], [authUserId, originOptions, repOptions]);
}

const csvColumns: ExportColumn[] = [
  { key: "origin", label: "Origin" },
  { key: "poNumber", label: "PO#" },
  { key: "bags", label: "Bags" },
  {
    key: "dateOfSale",
    label: "Date of Sale",
    format: (v: number) =>
      v
        ? new Date(v).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",
  },
  { key: "company", label: "Company" },
  { key: "clientName", label: "Client Name" },
  {
    key: "isEnterprise",
    label: "Enterprise",
    format: (v: unknown) => getEnterpriseBadgeLabel(v),
  },
  { key: "saleNumber", label: "Sale #" },
  { key: "rep", label: "Rep" },
  { key: "bagWeight", label: "Bag Wt (lb)" },
  { key: "totalWeight", label: "Total Wt (lb)" },
  { key: "salePrice", label: "Sale Price", format: formatCurrencyForExport },
  {
    key: "totalDollars",
    label: "Total Dollars",
    format: formatCurrencyForExport,
  },
  {
    key: "purchasePrice",
    label: "Purchase Price",
    format: formatCurrencyForExport,
  },
  { key: "difference", label: "Difference", format: formatCurrencyForExport },
  {
    key: "totalPurchase",
    label: "Total Purchase",
    format: formatCurrencyForExport,
  },
  { key: "profit", label: "Profit", format: formatCurrencyForExport },
];

const SCREENSHOT_ORIGINS = [
  "Colombia",
  "Ethiopia",
  "Guatemala",
  "Honduras",
  "Kenya",
  "Peru",
  "Rwanda",
] as const;

const SCREENSHOT_CLIENTS = [
  "Harbor Trading Co.",
  "Northline Supply Group",
  "Summit Distribution",
  "Driftwood Food Group",
  "Canyon Import Supply",
  "Blue Trail Holdings",
  "Morning Ledger LLC",
] as const;

const SCREENSHOT_REPS = [
  "A. Rivera",
  "M. Patel",
  "J. Kim",
] as const;

function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createSeededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randomInt(random: () => number, min: number, max: number): number {
  if (max <= min) return min;
  return Math.floor(random() * (max - min + 1)) + min;
}

function randomFloat(random: () => number, min: number, max: number): number {
  if (max <= min) return min;
  return random() * (max - min) + min;
}

function pickFromList<T>(random: () => number, values: readonly T[]): T {
  return values[Math.floor(random() * values.length)]!;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function anonymizeCommissionRow(
  row: CommissionRow,
  start: number,
  end: number,
  forceLoss: boolean,
): CommissionRow {
  const seed = hashString(
    `${row.id}|${row.poNumber}|${row.saleNumber}|${start}`,
  );
  const random = createSeededRandom(seed);

  const bagScale = randomFloat(random, 0.7, 1.35);
  const bags = Math.max(1, Math.round(Math.max(row.bags, 1) * bagScale));
  const bagWeightBase = row.bagWeight > 0 ? row.bagWeight : 150;
  const bagWeight = Math.max(
    120,
    Math.round(bagWeightBase * randomFloat(random, 0.9, 1.1)),
  );
  const totalWeight = bags * bagWeight;

  const salePriceBase = row.salePrice > 0 ? row.salePrice : 4.25;
  const salePrice = roundCurrency(
    Math.max(1.5, salePriceBase * randomFloat(random, 0.78, 1.28)),
  );
  const spread = forceLoss
    ? -roundCurrency(randomFloat(random, 0.08, 0.32))
    : roundCurrency(randomFloat(random, 0.2, 1.2));
  const purchasePrice = roundCurrency(Math.max(0, salePrice - spread));
  const difference = roundCurrency(salePrice - purchasePrice);

  const totalDollars = roundCurrency(totalWeight * salePrice);
  const totalPurchase = roundCurrency(totalWeight * purchasePrice);
  const profit = roundCurrency(totalDollars - totalPurchase);

  const dateOfSale = randomInt(random, start, end);
  const clientName = pickFromList(random, SCREENSHOT_CLIENTS);

  return {
    ...row,
    origin: pickFromList(random, SCREENSHOT_ORIGINS),
    poNumber: `PO-${randomInt(random, 1000, 9999)}`,
    bags,
    dateOfSale,
    clientName,
    company: clientName,
    saleNumber: `S-${randomInt(random, 10000, 99999)}`,
    rep: pickFromList(random, SCREENSHOT_REPS),
    bagWeight,
    totalWeight,
    salePrice,
    totalDollars,
    purchasePrice,
    difference,
    totalPurchase,
    profit,
  };
}

function pickLosingRowIds(rows: CommissionRow[], start: number): Set<string> {
  if (rows.length < 4) return new Set();

  const desiredLosses = rows.length < 14 ? 1 : 2;
  const ranked = rows
    .map((row) => ({
      id: row.id,
      score: hashString(`${row.id}|loss|${start}`),
    }))
    .sort((a, b) => a.score - b.score);

  return new Set(
    ranked.slice(0, Math.min(desiredLosses, rows.length)).map((entry) => entry.id),
  );
}

function anonymizeCommissionRows(
  rows: CommissionRow[],
  start: number,
  end: number,
): CommissionRow[] {
  const losingIds = pickLosingRowIds(rows, start);
  return rows.map((row) =>
    anonymizeCommissionRow(row, start, end, losingIds.has(row.id)),
  );
}

export function CommissionsTab({ authUserId, initialMonth }: CommissionsTabProps) {
  const monthOptions = React.useMemo(() => buildMonthOptions(), []);
  const validMonthValues = React.useMemo(() => new Set(monthOptions.map((o) => o.value)), [monthOptions]);
  const [selectedMonth, setSelectedMonth] = React.useState(
    initialMonth && validMonthValues.has(initialMonth) ? initialMonth : monthOptions[0]!.value,
  );
  const [screenshotModeEnabled, setScreenshotModeEnabled] =
    React.useState(false);
  const isDev = ENV.IS_DEV;

  const { start, end } = React.useMemo(
    () => getMonthRange(selectedMonth),
    [selectedMonth],
  );

  const data = useQuery(
    api.admin.getCommissionLineItems,
    authUserId
      ? { authUserId, startDate: start, endDate: end }
      : "skip",
  ) as CommissionRow[] | undefined;

  const loading = data === undefined;
  const displayData = React.useMemo(() => {
    if (!data) return [];
    if (!isDev || !screenshotModeEnabled) return data;
    return anonymizeCommissionRows(data, start, end);
  }, [data, end, isDev, screenshotModeEnabled, start]);

  const columns = useColumns(displayData, authUserId);

  const totals = React.useMemo(() => {
    if (displayData.length === 0)
      return {
        bags: 0,
        weight: 0,
        revenue: 0,
        cost: 0,
        profit: 0,
        margin: 0,
      };
    const bags = displayData.reduce((s, r) => s + r.bags, 0);
    const weight = displayData.reduce((s, r) => s + r.totalWeight, 0);
    const revenue = displayData.reduce((s, r) => s + r.totalDollars, 0);
    const cost = displayData.reduce((s, r) => s + r.totalPurchase, 0);
    const profit = displayData.reduce((s, r) => s + r.profit, 0);
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
    return { bags, weight, revenue, cost, profit, margin };
  }, [displayData]);

  const lineItemStats = React.useMemo(() => {
    if (displayData.length === 0) {
      return {
        rows: 0,
        profitable: 0,
        losing: 0,
        breakEven: 0,
        repCount: 0,
        originCount: 0,
      };
    }

    let profitable = 0;
    let losing = 0;
    let breakEven = 0;
    const reps = new Set<string>();
    const origins = new Set<string>();

    for (const row of displayData) {
      if (row.profit > 0) profitable += 1;
      else if (row.profit < 0) losing += 1;
      else breakEven += 1;

      if (row.rep) reps.add(row.rep);
      if (row.origin) origins.add(row.origin);
    }

    return {
      rows: displayData.length,
      profitable,
      losing,
      breakEven,
      repCount: reps.size,
      originCount: origins.size,
    };
  }, [displayData]);

  const selectedLabel =
    monthOptions.find((o) => o.value === selectedMonth)?.label ?? "";

  const handleExport = React.useCallback(() => {
    if (displayData.length === 0) {
      toast.error("No commissions selected");
      return;
    }
    exportToCSV(displayData, csvColumns, `commissions-${selectedMonth}.csv`);
  }, [displayData, selectedMonth]);

  const handleExportSelected = React.useCallback((selectedRows: CommissionRow[]) => {
    if (selectedRows.length === 0) {
      toast.error("No commissions selected");
      return;
    }
    exportToCSV(
      selectedRows,
      csvColumns,
      `commissions-selected-${selectedMonth}.csv`,
    );
  }, [selectedMonth]);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col items-end gap-2">
          {isDev ? (
            <div className="flex items-center gap-2">
              <Switch
                id="commissions-screenshot-mode"
                checked={screenshotModeEnabled}
                onCheckedChange={setScreenshotModeEnabled}
              />
              <Label
                htmlFor="commissions-screenshot-mode"
                className="text-xs text-muted-foreground"
              >
                Screenshot mode
              </Label>
            </div>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={displayData.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Export All
          </Button>
        </div>
      </div>

      {!loading && displayData.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {screenshotModeEnabled ? (
            <Badge variant="outline" className="border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200">
              Dev screenshot mode: randomized data
            </Badge>
          ) : null}
          <Badge variant="outline" className="border-border/80 bg-muted/30 text-foreground">
            {selectedLabel}
          </Badge>
          <Badge variant="outline" className="border-border/80 bg-muted/30 text-foreground">
            {lineItemStats.rows.toLocaleString()} line items
          </Badge>
          <Badge variant="outline" className="border-border/80 bg-muted/30 text-foreground">
            {lineItemStats.originCount.toLocaleString()} origins
          </Badge>
          <Badge variant="outline" className="border-border/80 bg-muted/30 text-foreground">
            {lineItemStats.repCount.toLocaleString()} reps
          </Badge>
          <Badge variant="outline" className={profitBadgeClass(lineItemStats.profitable)}>
            {lineItemStats.profitable.toLocaleString()} profitable
          </Badge>
          {lineItemStats.losing > 0 ? (
            <Badge variant="outline" className={profitBadgeClass(-lineItemStats.losing)}>
              {lineItemStats.losing.toLocaleString()} losing
            </Badge>
          ) : (
            <Badge variant="outline" className="border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200">
              No losing deals
            </Badge>
          )}
          {lineItemStats.breakEven > 0 ? (
            <Badge variant="outline" className={profitBadgeClass(0)}>
              {lineItemStats.breakEven.toLocaleString()} break-even
            </Badge>
          ) : null}
          <Badge variant="outline" className={marginBadgeClass(totals.margin)}>
            Margin {totals.margin.toFixed(1)}%
          </Badge>
        </div>
      ) : null}

      {/* Summary Cards */}
      {!loading && displayData.length > 0 && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <InternalMetricCard
            title="Total Bags"
            value={totals.bags.toLocaleString()}
            hint={`${selectedLabel}`}
            icon={Boxes}
          />
          <InternalMetricCard
            title="Total Weight"
            value={`${totals.weight.toLocaleString()} lb`}
            hint={`${selectedLabel}`}
            icon={Scale}
          />
          <InternalMetricCard
            title="Total Revenue"
            value={formatCurrency(totals.revenue)}
            hint={`${selectedLabel}`}
            icon={DollarSign}
          />
          <InternalMetricCard
            title="Total Cost"
            value={formatCurrency(totals.cost)}
            hint={`${selectedLabel}`}
            icon={DollarSign}
          />
          <InternalMetricCard
            title="Total Profit"
            value={formatCurrency(totals.profit)}
            hint={`${selectedLabel}`}
            icon={TrendingUp}
          />
          <InternalMetricCard
            title="Margin"
            value={`${totals.margin.toFixed(1)}%`}
            hint={`${selectedLabel}`}
            icon={Percent}
          />
        </div>
      )}

      {/* Data Table */}
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : displayData.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center rounded-md border border-dashed">
          <p className="text-sm text-muted-foreground">
            No commission data for {selectedLabel}
          </p>
        </div>
      ) : (
        <InternalEntityDataTable
          data={displayData}
          columns={columns}
          pageSize={25}
          actionBar={({ selectedRows, clearSelection }) => (
            <DataTableFloatingBar
              selectedCount={selectedRows.length}
              onClear={clearSelection}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleExportSelected(selectedRows)}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Export Selected
              </Button>
            </DataTableFloatingBar>
          )}
        />
      )}
    </div>
  );
}
